import { __assign, __read, __rest, __spreadArray } from "tslib";
import React, { useCallback, useState } from "react";
import { Box, Card, IconButton, LinearProgress, RootRef, Typography, alpha, createStyles, makeStyles } from "@material-ui/core";
import { Delete, VideoLibrary } from "@material-ui/icons";
import cn from "classnames";
import { useConfirm } from "material-ui-confirm";
import { useDropzone } from "react-dropzone";
import { useDispatch } from "react-redux";
var useStyles = makeStyles(function (theme) {
    return createStyles({
        root: {
            height: 250,
            background: theme.palette.background.paper,
            border: "3px dashed ".concat(theme.palette.colors.info[750]),
            color: alpha(theme.palette.text.primary, 0.6),
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center"
        },
        dropZone: {
            background: theme.palette.background.default,
            width: "calc(100% + 50px)",
            height: "calc(100% + 50px)",
            margin: -50,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: theme.spacing(3),
            transition: "all 0.2s",
            cursor: function (_a) {
                var loading = _a.loading, disabled = _a.disabled;
                return loading || disabled ? "not-allowed" : "pointer";
            }
        },
        compact: {
            height: 80,
            margin: 0,
            padding: 0
        },
        dragActive: {
            background: theme.palette.colors.basic[700]
        },
        fileNameWrapper: {
            backgroundColor: theme.palette.background.paper,
            padding: theme.spacing(1),
            margin: theme.spacing(1, 0),
            maxWidth: 150,
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis"
        },
        deleteButton: {
            color: theme.palette.colors.danger[500],
            backgroundColor: theme.palette.colors.danger[200],
            marginLeft: theme.spacing(2)
        },
        progress: {
            background: "hsl(0,0%,10%)"
        },
        dialog: {
            padding: theme.spacing(2),
            background: theme.palette.colors.basic[900]
        },
        successButton: {
            height: "48px",
            background: theme.palette.colors.danger[900]
        },
        sucessButtonLabel: {
            fontSize: "16px",
            fontWeight: "bold",
            lineHeight: 1.25,
            color: theme.palette.colors.danger[500]
        },
        cancellButton: {
            height: "48px",
            borderRadius: "4px",
            border: "1px solid ".concat(theme.palette.colors.basic[500]),
            background: "transparent"
        },
        cancelButtonLabel: {
            fontSize: "16px",
            fontWeight: "bold",
            lineHeight: 1.25,
            color: theme.palette.colors.basic[1250]
        }
    });
});
var Uploader = function (_a) {
    var _b;
    var accept = _a.accept, _c = _a.boxProps, boxProps = _c === void 0 ? {} : _c, clearFileInputSubject = _a.clearFileInputSubject, _d = _a.title, title = _d === void 0 ? "Drag and drop the file here, or click to select file" : _d, _e = _a.icon, icon = _e === void 0 ? React.createElement(VideoLibrary, { style: { width: "64px", height: "64px" } }) : _e, error = _a.error, onAdd = _a.onAdd, onRemove = _a.onRemove, progress = _a.progress, confirmDescription = _a.confirmDescription, _f = _a.multiple, multiple = _f === void 0 ? false : _f, _g = _a.loading, loading = _g === void 0 ? false : _g, _h = _a.header, header = _h === void 0 ? "" : _h, _j = _a.confirmTitle, confirmTitle = _j === void 0 ? "Are sure you want to delete uploaded file?" : _j, _k = _a.cancellText, cancellText = _k === void 0 ? "No, Continue Uploading" : _k, _l = _a.confirmText, confirmText = _l === void 0 ? "Yes, I’m Sure" : _l, dragFileError = _a.dragFileError, _m = _a.disabled, disabled = _m === void 0 ? false : _m, emit = _a.emit;
    var dispatch = useDispatch();
    var classes = useStyles({ loading: loading, disabled: disabled });
    var _o = __read(useState([]), 2), files = _o[0], setFiles = _o[1];
    var _p = __read(useState(false), 2), dragActive = _p[0], setDragActive = _p[1];
    var confirmPopUp = useConfirm();
    var onDelete = function (name) {
        confirmPopUp({
            title: confirmTitle,
            description: confirmDescription,
            dialogProps: { classes: { paper: classes.dialog } },
            cancellationButtonProps: {
                variant: "outlined",
                color: "secondary",
                classes: {
                    root: classes.cancellButton,
                    label: classes.cancelButtonLabel
                }
            },
            confirmationButtonProps: {
                variant: "contained",
                classes: {
                    root: classes.successButton,
                    label: classes.sucessButtonLabel
                }
            },
            cancellationText: cancellText,
            confirmationText: confirmText
        }).then(function () {
            var _files = files.filter(function (i) { return i.name !== name; });
            var _file = files.find(function (i) { return i.name === name; });
            setFiles(_files);
            onRemove && onRemove(_file);
        });
    };
    var onDragEnter = function () {
        setDragActive(true);
    };
    var onDragLeave = function () {
        setDragActive(false);
    };
    var onDrop = useCallback(function (acceptedFiles) {
        var _files = __spreadArray(__spreadArray([], __read(files), false), __read(acceptedFiles), false);
        if (_files.length > 0) {
            setFiles(_files);
            onAdd && onAdd(_files);
        }
        else {
            emit &&
                dispatch(emit({
                    message: dragFileError || "Sorry!, only video file formats are supported.",
                    color: dragFileError ? "error" : "warning"
                }));
        }
        setDragActive(false);
    }, [dispatch]);
    var _q = useDropzone({
        onDrop: onDrop,
        onDragEnter: onDragEnter,
        onDragLeave: onDragLeave,
        onDropAccepted: onDragLeave,
        multiple: multiple,
        accept: accept
    }), getRootProps = _q.getRootProps, getInputProps = _q.getInputProps;
    var _r = getRootProps(), ref = _r.ref, rootProps = __rest(_r, ["ref"]);
    if (clearFileInputSubject) {
        clearFileInputSubject.subscribe(function () { return setFiles([]); });
    }
    return (React.createElement(Card, { className: classes.root, style: boxProps },
        React.createElement(RootRef, { rootRef: ref },
            React.createElement(Box, __assign({}, rootProps, { className: cn(classes.dropZone, (_b = {},
                    _b[classes.dragActive] = dragActive,
                    _b[classes.compact] = files.length !== 0,
                    _b)) }),
                React.createElement(Box, { py: 1 }, icon),
                React.createElement("input", __assign({}, getInputProps(), { disabled: loading || disabled })),
                !loading && header && React.createElement("h3", null, header),
                files.length === 0 &&
                    (dragActive ? (React.createElement(Typography, { variant: "subtitle2" }, "Drop files here")) : (React.createElement(Typography, { variant: "subtitle2" }, title))))),
        files.length === 0 && loading && (React.createElement(Box, { width: "100%" },
            React.createElement(LinearProgress, { className: classes.progress, color: "primary", variant: progress ? "determinate" : "indeterminate", value: progress }),
            typeof progress === "number" ? React.createElement(Box, null,
                Math.floor(progress),
                "%") : null)),
        React.createElement(Box, { display: "flex", flexDirection: "column", maxHeight: 140 },
            error && (React.createElement(Box, { my: 3 },
                React.createElement(Typography, { color: "error", variant: "body2" }, typeof error === "string" ? error : "Error happened, try refreshing the page or retry later"))),
            !error &&
                files.map(function (file, idx) { return (React.createElement(Box, { key: "".concat(file.name, "_").concat(idx) },
                    React.createElement(Box, { display: "flex", alignItems: "center", justifyContent: "space-between" },
                        React.createElement(Box, { className: classes.fileNameWrapper },
                            React.createElement(Typography, { variant: "caption" }, file.name)),
                        React.createElement(IconButton, { disabled: loading || disabled, onClick: function (e) {
                                e.stopPropagation();
                                e.preventDefault();
                                onDelete(file.name);
                            }, size: "small", className: classes.deleteButton },
                            React.createElement(Delete, null))),
                    loading && (React.createElement(Box, null,
                        React.createElement(LinearProgress, { className: classes.progress, color: "primary", variant: progress ? "determinate" : "indeterminate", value: progress }),
                        typeof progress === "number" ? React.createElement(Box, null,
                            Math.floor(progress),
                            "%") : null)))); }))));
};
export default Uploader;
//# sourceMappingURL=index.js.map